import { ApiKeysContent } from "@/features/api-keys/pages/ApiKeysPage";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ChangelogContent } from "@/features/changelog/pages/ChangelogPage";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { DangerZoneCard } from "../components/DangerZoneCard";
import { PlanCard } from "../components/PlanCard";
import { UsageLimitsCard } from "../components/UsageLimitsCard";
import { UserProfileCard } from "../components/UserProfileCard";

type SettingsTab = "account" | "keys" | "changelog";

const TABS: Array<{ id: SettingsTab; label: string; title: string; blurb: string }> = [
  { id: "account", label: "account", title: "Account", blurb: "your profile, usage, and data" },
  { id: "keys", label: "API keys", title: "API keys", blurb: "programmatic access to Paperclip" },
  { id: "changelog", label: "changelog", title: "Changelog", blurb: "what changed in each release" },
];

const isTab = (v: string | undefined): v is SettingsTab => TABS.some((t) => t.id === v);

/**
 * Settings in the editorial register: one display heading, a quiet mono tab
 * row (same styling as the hero's scene switcher), and the section below.
 * API keys and the changelog live here (account menu). Feature flags are a
 * local-dev sidebar destination, not a Settings tab.
 */
export const SettingsPage = () => {
  const { user } = useAuth();
  const { tab: rawTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const tab: SettingsTab = isTab(rawTab) ? rawTab : "account";
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];
  useDocumentTitle(`Settings · ${active.title}`);

  if (rawTab && !isTab(rawTab)) return <Navigate to="/settings" replace />;

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-sm text-zinc-500">Not logged in</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="editorial-frame editorial-frame-home mx-auto">
        <header>
          <h1 className="font-display text-4xl leading-[1.1] tracking-tight text-zinc-900">
            Settings
          </h1>
          <p className="chat-ed-meta !text-sm">
            <span className="hero-mono text-xs">{user.email ?? user.full_name ?? "signed in"}</span> · {active.blurb}
          </p>
        </header>

        <nav className="hero-scene-nav pt-8" aria-label="Settings sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`hero-scene-tab${t.id === tab ? " is-active" : ""}`}
              aria-current={t.id === tab ? "page" : undefined}
              onClick={() => navigate(t.id === "account" ? "/settings" : `/settings/${t.id}`)}
            >
              {t.id === "keys" ? "API keys" : t.label}
            </button>
          ))}
        </nav>

        <section className="mt-10 max-w-3xl" aria-labelledby={`settings-${tab}`}>
          <h2 id={`settings-${tab}`} className="sr-only">
            {active.title}
          </h2>
          {tab === "account" && (
            <div>
              <UserProfileCard />
              {flags.billing && <PlanCard />}
              <UsageLimitsCard />
              <DangerZoneCard />
            </div>
          )}
          {tab === "keys" && <ApiKeysContent />}
          {tab === "changelog" && <ChangelogContent className="mt-0" />}
        </section>
      </div>
    </div>
  );
};
